import { Group, Card, Text, Title } from "@mantine/core";
import { useCallback } from "react";

export type PortalReportStatus = "new" | "viewed" | "available";

interface PortalStatusCardsProps {
  counts: {
    new: number;
    available: number;
    viewed: number;
    total: number;
  };
  activeStatus: string;
  onStatusChange: (status: string) => void;
}

export function PortalStatusCards({
  counts,
  activeStatus,
  onStatusChange,
}: PortalStatusCardsProps) {
  const handleCardClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const status = event.currentTarget.getAttribute("data-status") || "";
      onStatusChange(activeStatus === status ? "" : status);
    },
    [activeStatus, onStatusChange]
  );

  return (
    <Group grow gap="md" wrap="wrap">
      {/* New Reports Card */}
      <Card
        withBorder
        padding="md"
        radius="md"
        data-status="new"
        onClick={handleCardClick}
        style={{
          cursor: "pointer",
          transition: "transform 0.15s ease, box-shadow 0.15s ease",
          borderLeft: "4px solid var(--mantine-color-orange-5)",
          backgroundColor:
            activeStatus === "new"
              ? "var(--mantine-color-orange-0)"
              : "var(--mantine-color-white)",
        }}
      >
        <Text
          size="xs"
          fw={700}
          c="orange.6"
          style={{ textTransform: "uppercase", letterSpacing: "0.05em" }}
        >
          New Reports
        </Text>
        <Group align="flex-end" justify="space-between" mt="xs">
          <Title order={2} style={{ fontSize: "1.8rem", lineHeight: 1 }}>
            {counts.new}
          </Title>
          <Text size="xs" c="dimmed">
            Unviewed since login
          </Text>
        </Group>
      </Card>

      {/* Available Reports Card */}
      <Card
        withBorder
        padding="md"
        radius="md"
        data-status="available"
        onClick={handleCardClick}
        style={{
          cursor: "pointer",
          transition: "transform 0.15s ease, box-shadow 0.15s ease",
          borderLeft: "4px solid var(--mantine-color-teal-5)",
          backgroundColor:
            activeStatus === "available"
              ? "var(--mantine-color-teal-0)"
              : "var(--mantine-color-white)",
        }}
      >
        <Text
          size="xs"
          fw={700}
          c="teal.6"
          style={{ textTransform: "uppercase", letterSpacing: "0.05em" }}
        >
          Available Reports
        </Text>
        <Group align="flex-end" justify="space-between" mt="xs">
          <Title order={2} style={{ fontSize: "1.8rem", lineHeight: 1 }}>
            {counts.available}
          </Title>
          <Text size="xs" c="dimmed">
            Unviewed prior reports
          </Text>
        </Group>
      </Card>

      {/* Viewed Reports Card */}
      <Card
        withBorder
        padding="md"
        radius="md"
        data-status="viewed"
        onClick={handleCardClick}
        style={{
          cursor: "pointer",
          transition: "transform 0.15s ease, box-shadow 0.15s ease",
          borderLeft: "4px solid var(--mantine-color-gray-5)",
          backgroundColor:
            activeStatus === "viewed"
              ? "var(--mantine-color-gray-0)"
              : "var(--mantine-color-white)",
        }}
      >
        <Text
          size="xs"
          fw={700}
          c="gray.6"
          style={{ textTransform: "uppercase", letterSpacing: "0.05em" }}
        >
          Viewed Reports
        </Text>
        <Group align="flex-end" justify="space-between" mt="xs">
          <Title order={2} style={{ fontSize: "1.8rem", lineHeight: 1 }}>
            {counts.viewed}
          </Title>
          <Text size="xs" c="dimmed">
            Opened at least once
          </Text>
        </Group>
      </Card>
    </Group>
  );
}

export default PortalStatusCards;
